import { FC, MouseEvent, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import CircleInfoIcon from '@/storybook/icons/CircleInfoIcon';
import CrossIcon from '@/storybook/icons/CrossIcon';
import Button from '../Button/Button';
import FilterChipTooltip from '../FilterChipTooltip/FilterChipTooltip';
import IconButton from '../IconButton/IconButton';
import Text from '../Text/Text';
import styles from './FilterChip.module.css';

export interface FilterChipBaseProps {
  'label': string;
  'size'?: 'M' | 'S';
  'isSelected'?: boolean;
  'isDisabled'?: boolean;
  'isError'?: boolean;
  'leadingIcon'?: ReactNode;
  'content'?: ReactNode;
  'onCloseClick'?: VoidFunction;
  'className'?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

// onButtonClick without buttonLabel is a no-op — the button only renders when both are provided.
export interface FilterChipProps extends FilterChipBaseProps {
  onButtonClick?: VoidFunction;
  buttonLabel?: string;
  tooltip?: string;
  /** Anchors `tooltip` to a `FilterChipTooltip` rendered once by the parent instead of mounting one per chip. */
  sharedTooltipId?: string;
}

const FilterChip: FC<FilterChipProps> = ({
  label,
  size = 'M',
  isSelected = false,
  isDisabled = false,
  isError = false,
  leadingIcon,
  content,
  onCloseClick,
  onButtonClick,
  buttonLabel,
  tooltip,
  sharedTooltipId,
  className,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'filter-chip',
}) => {
  const { t } = useTranslation();
  const tooltipId = useId();

  const handleCloseClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCloseClick?.();
  };

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onButtonClick?.();
  };

  return (
    <div
      className={cx(
        className,
        styles.filterChipContainer,
        size === 'S' ? styles.sizeS : styles.sizeM,
        isSelected && styles.selected,
        isDisabled && styles.disabled,
      )}
      data-tooltip-id={tooltip ? (sharedTooltipId ?? tooltipId) : undefined}
      data-tooltip-content={tooltip && sharedTooltipId ? tooltip : undefined}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {isError ? (
        <span className={cx(styles.iconSlot, styles.errorIcon)} aria-hidden="true" data-testid="filter-chip-error-icon">
          <CircleInfoIcon />
        </span>
      ) : null}

      {leadingIcon ? (
        <span className={styles.iconSlot} aria-hidden="true">
          {leadingIcon}
        </span>
      ) : null}

      <Text>{label}</Text>

      {content}

      {onButtonClick && buttonLabel ? (
        <Button variant="text" size={'XS'} disabled={isDisabled} onClick={handleButtonClick}>
          {buttonLabel}
        </Button>
      ) : null}

      {onCloseClick ? (
        <IconButton
          appearance="text"
          size="S"
          icon={<CrossIcon />}
          disabled={isDisabled}
          onClick={handleCloseClick}
          className={styles.closeButton}
          aria-label={t('global.filterChip.removeAriaLabel')}
        />
      ) : null}

      {tooltip && !sharedTooltipId ? <FilterChipTooltip id={tooltipId} content={tooltip} /> : null}
    </div>
  );
};

export default FilterChip;
